import { z } from 'zod';

/** As flags do feature-flag-service (`currentFeatures(system: USER_APP)`) que a wallet de
 *  despesas lê. `wallets_module` é a MESMA chave com que o app Flutter libera a wallet do
 *  legado (`packages/expenses/common/lib/src/shared/constants/feature_flags.dart`). O
 *  customer-api repassa só estas; o resto da lista do legado não sai de lá. */
export const VOLL_WALLET_FEATURE_FLAGS = ['wallets_module'] as const;

export interface VollWalletAccessInput {
  /** `policyAccess` contém `wallet`; `null` = não resolvido. */
  walletPolicy: boolean | null;
  /** `currentFeatures(USER_APP)` filtradas; `null` = não resolvido. */
  featureFlags: readonly string[] | null;
  /** A empresa estourou o limite de gastos (`spendingBalanceOverflow`); `null` = não resolvido
   *  ou não consultado. */
  spendingBlocked?: boolean | null;
}

// `unavailable` só acompanha `offer: false`: a leitura que decide (flags ou política) falhou e o
// que se SABE aponta para a carteira — a tela avisa e relê em vez de sumir com ela (#6450).
export const VollWalletEntrySchema = z
  .object({ offer: z.boolean(), unavailable: z.literal(true).optional() })
  .strict();
export type VollWalletEntry = z.infer<typeof VollWalletEntrySchema>;

/** Por que a carteira não foi oferecida — o motivo do log/contador do `/me` (#6450). */
export type VollWalletHiddenReason =
  | 'no_policy'
  | 'policy_unresolved'
  | 'missing_flag'
  | 'flags_unresolved'
  | 'spending_blocked';

export interface VollWalletVerdict {
  entry: VollWalletEntry;
  /** Ausente quando oferece. */
  reason?: VollWalletHiddenReason;
}

const HIDDEN: VollWalletEntry = { offer: false };
const UNAVAILABLE: VollWalletEntry = { offer: false, unavailable: true };

/**
 * Se a wallet de despesas aparece: a regra do módulo do legado (`VollWalletModuleSettings`:
 * política `wallet` + flag `wallets_module` + `hasValidSpendingLimit`). Política ou flags não resolvidas NUNCA oferecem; o bloqueio de gastos só esconde
 * quando CONFIRMADO — sem resposta do bil-bff a wallet segue, como o saldo não some por uma
 * falha de outro serviço.
 *
 * `unavailable` (fail-closed, #6450): só quando NENHUMA leitura resolvida descarta a carteira e
 * ao menos uma aponta para ela — política `wallet` com as flags fora, ou a flag com a política
 * fora. Com as duas leituras fora não se sabe nada, e a carteira só some: o aviso de
 * "indisponível" não pode aparecer para quem nunca teve carteira.
 */
export function vollWalletVerdict(input: VollWalletAccessInput): VollWalletVerdict {
  const { walletPolicy, featureFlags, spendingBlocked } = input;
  if (spendingBlocked === true) return { entry: HIDDEN, reason: 'spending_blocked' };
  if (walletPolicy === false) return { entry: HIDDEN, reason: 'no_policy' };
  if (featureFlags && !VOLL_WALLET_FEATURE_FLAGS.every((flag) => featureFlags.includes(flag)))
    return { entry: HIDDEN, reason: 'missing_flag' };
  if (walletPolicy === null)
    return { entry: featureFlags ? UNAVAILABLE : HIDDEN, reason: 'policy_unresolved' };
  if (featureFlags === null) return { entry: UNAVAILABLE, reason: 'flags_unresolved' };
  return { entry: { offer: true } };
}

export function vollWalletEntry(input: VollWalletAccessInput): VollWalletEntry {
  return vollWalletVerdict(input).entry;
}
